import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { hasRole } from '@death-star/shared';
import { useApiQuery } from '../use-api.js';
import { useAuth } from '../auth.js';
import { useRole } from '../use-role.js';
import { apiFetch, UnauthorizedError } from '../api-client.js';
import { pollNowButtonState, POLL_NOW_WINDOW_MS } from './incidents-poll-button-state.js';
import { ConceptHelpIcon } from '../components/ConceptHelpIcon.js';

// Source enum + arch_group enum match the API contract in
// packages/api/src/handlers/api-incidents.ts.
type IncidentSource = 'confluence-postmortem' | 'pagerduty-statuspage';
type ArchGroup = 'edge' | 'engine' | 'hero';

interface IncidentListItem {
  incident_id: string;
  source: IncidentSource;
  source_url: string;
  title: string;
  arch_group: ArchGroup | null;
  occurred_at: string;
  severity: string | null;
  referenced_pd_alarm_url: string | null;
  link_count: number;
  high_confidence_link_count: number;
  has_bot_approved_linked_pr: boolean;
}

interface IncidentListResponse {
  incidents: IncidentListItem[];
  truncated: boolean;
}

interface IncidentDetailLinkedPr {
  org: string;
  repo: string;
  pr_number: number;
  confidence: number;
  reason: string;
  linked_at: string;
  has_bot_approved_artifact: boolean;
}

interface IncidentDetailRecord {
  pk: string;
  source: IncidentSource;
  source_url: string;
  title: string;
  arch_group: ArchGroup | null;
  impacted_business_services: string[];
  occurred_at: string;
  resolved_at: string | null;
  severity: string | null;
  referenced_pd_alarm_url: string | null;
  ingested_at: string;
}

interface IncidentDetailResponse {
  incident: IncidentDetailRecord;
  linked_prs: IncidentDetailLinkedPr[];
}

const inputStyle: CSSProperties = {
  padding: '8px 12px',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-sm)',
  fontSize: '0.875rem',
  color: 'var(--loop-neutral-1100)',
  background: 'var(--loop-neutral-0)',
};

// Time-range options translate to the API's `since` ISO string. Keeping the
// translation client-side (instead of asking the API to know the buckets)
// makes the filter UI flexible without an API change.
const TIME_RANGES: { value: '7' | '30' | '90' | 'all'; label: string }[] = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

function sinceForRange(range: '7' | '30' | '90' | 'all'): string | null {
  if (range === 'all') return null;
  const days = Number(range);
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
}

function relativeTime(iso: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const deltaSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const min = Math.round(deltaSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  const yr = Math.round(day / 365);
  return `${yr}y ago`;
}

function truncate(s: string, max = 50): string {
  if (!s) return '';
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}

// Lightweight inline icons. Kept local instead of pulling in LinkedIncidents
// (Phase 3 Task 10 review feedback prefers small inline duplication over a
// premature shared component — LinkedIncidents' chip cluster is per-PR and
// renders differently from a per-row source icon here).
function ConfluenceIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-label="Confluence" role="img">
      <path fill="#2684ff" d="M2.23,49.53c-.65,1.06-1.38,2.29-2,3.27a2,2,0,0,0,.67,2.72l13,8a2,2,0,0,0,2.77-.68c.52-.87,1.19-2,1.92-3.21,5.15-8.5,10.33-7.46,19.67-3l12.89,6.13a2,2,0,0,0,2.69-1l6.19-14a2,2,0,0,0-1-2.62c-2.72-1.28-8.13-3.83-13-6.18C28.51,30.45,13.62,31,2.23,49.53Z" />
      <path fill="#0052cc" d="M60.52,17.76c.65-1.06,1.38-2.29,2-3.27a2,2,0,0,0-.67-2.72l-13-8A2,2,0,0,0,46,4.43c-.52.87-1.19,2-1.92,3.21-5.15,8.5-10.33,7.46-19.67,3L11.56,4.54a2,2,0,0,0-2.69,1l-6.19,14a2,2,0,0,0,1,2.62c2.72,1.28,8.13,3.83,13,6.18C34.24,36.84,49.13,36.27,60.52,17.76Z" />
    </svg>
  );
}

function PagerDutyIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-label="PagerDuty" role="img">
      <path fill="#06ac38" d="M6.704 59.217H0v-33.65c0-3.455 1.418-5.544 2.604-6.704 2.63-2.58 6.2-2.656 6.782-2.656h10.546c3.765 0 5.93 1.52 7.117 2.8 2.346 2.553 2.372 5.853 2.32 6.73v12.687c0 3.662-1.496 5.828-2.733 6.988-2.553 2.398-5.93 2.45-6.73 2.424H6.704zm13.46-18.102c.36 0 1.367-.103 1.908-.62.413-.387.62-1.083.62-2.1v-13.02c0-.36-.077-1.315-.593-1.857-.5-.516-1.444-.62-2.166-.62h-10.6c-2.63 0-2.63 1.985-2.63 2.656v15.55zM57.296 4.783H64V38.46c0 3.455-1.418 5.544-2.604 6.704-2.63 2.58-6.2 2.656-6.782 2.656H44.068c-3.765 0-5.93-1.52-7.117-2.8-2.346-2.553-2.372-5.853-2.32-6.73V25.62c0-3.662 1.496-5.828 2.733-6.988 2.553-2.398 5.93-2.45 6.73-2.424h13.202zM43.836 22.9c-.36 0-1.367.103-1.908.62-.413.387-.62 1.083-.62 2.1v13.02c0 .36.077 1.315.593 1.857.5.516 1.444.62 2.166.62h10.598c2.656-.026 2.656-2 2.656-2.682V22.9z" />
    </svg>
  );
}

function SourceIcon({ source }: { source: IncidentSource }) {
  return source === 'pagerduty-statuspage' ? <PagerDutyIcon /> : <ConfluenceIcon />;
}

function ConfidenceStars({ confidence }: { confidence: number }) {
  const stars = Math.max(0, Math.min(5, Math.round(confidence / 20)));
  return (
    <span title={`${confidence}% confidence`} style={{ fontSize: '0.75rem', letterSpacing: '1px', lineHeight: 1 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          style={{ color: i < stars ? 'var(--loop-yellow-800)' : 'var(--loop-neutral-400)' }}
        >
          ★
        </span>
      ))}
    </span>
  );
}

function ArchGroupChip({ arch }: { arch: ArchGroup | null }) {
  if (!arch) return <span style={{ color: 'var(--loop-neutral-500)' }}>—</span>;
  const bg = arch === 'edge' ? 'var(--loop-blue-100)' : arch === 'engine' ? 'var(--loop-yellow-100)' : 'var(--loop-green-100)';
  const fg = arch === 'edge' ? 'var(--loop-blue-700)' : arch === 'engine' ? 'var(--loop-yellow-800)' : 'var(--loop-green-700)';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 'var(--loop-radius-xs)',
        background: bg,
        color: fg,
        fontSize: '0.6875rem',
        fontWeight: 528,
        textTransform: 'capitalize',
      }}
    >
      {arch}
    </span>
  );
}

function SeverityBadge({ severity }: { severity: string | null }) {
  if (!severity) return <span style={{ color: 'var(--loop-neutral-500)' }}>—</span>;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 'var(--loop-radius-xs)',
        background: 'var(--loop-red-100)',
        color: 'var(--loop-red-700)',
        fontSize: '0.6875rem',
        fontWeight: 528,
      }}
    >
      {severity}
    </span>
  );
}

function IncidentDetailPanel({ incidentId }: { incidentId: string }) {
  const { data, isLoading, error } = useApiQuery<IncidentDetailResponse>(
    ['incident-detail', incidentId],
    `/api/incidents/${encodeURIComponent(incidentId)}`
  );

  if (isLoading) {
    return <div style={{ padding: 16, color: 'var(--loop-neutral-700)' }}>Loading…</div>;
  }
  if (error) {
    return <div style={{ padding: 16 }} className="error">Error: {String(error)}</div>;
  }
  if (!data) return null;

  const { incident, linked_prs } = data;
  return (
    <div style={{ padding: 16, background: 'var(--loop-neutral-100)', borderTop: '1px solid var(--loop-neutral-200)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: '0.6875rem', color: 'var(--loop-neutral-600)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Title</div>
          <a
            href={incident.source_url}
            target="_blank"
            rel="noreferrer"
            style={{ color: 'var(--loop-blue-700)', fontSize: '0.875rem' }}
          >
            {incident.title}
          </a>
        </div>
        <div>
          <div style={{ fontSize: '0.6875rem', color: 'var(--loop-neutral-600)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Arch group</div>
          <div><ArchGroupChip arch={incident.arch_group} /></div>
        </div>
        <div>
          <div style={{ fontSize: '0.6875rem', color: 'var(--loop-neutral-600)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Severity</div>
          <div><SeverityBadge severity={incident.severity} /></div>
        </div>
        <div>
          <div style={{ fontSize: '0.6875rem', color: 'var(--loop-neutral-600)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Impacted services</div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--loop-neutral-1000)' }}>
            {incident.impacted_business_services.length === 0
              ? <span style={{ color: 'var(--loop-neutral-500)' }}>—</span>
              : incident.impacted_business_services.join(', ')}
          </div>
        </div>
        {incident.referenced_pd_alarm_url && (
          <div>
            <div style={{ fontSize: '0.6875rem', color: 'var(--loop-neutral-600)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>PD alarm</div>
            <a
              href={incident.referenced_pd_alarm_url}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--loop-red-700)', fontSize: '0.8125rem' }}
            >
              Open in PagerDuty →
            </a>
          </div>
        )}
      </div>
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: '0.6875rem', color: 'var(--loop-neutral-600)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
          Linked PRs ({linked_prs.length})
        </div>
        {linked_prs.length === 0 ? (
          <p style={{ fontSize: '0.8125rem', color: 'var(--loop-neutral-500)' }}>No linked PRs.</p>
        ) : (
          <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--loop-neutral-700)' }}>
                <th style={{ padding: '4px 8px' }}>PR</th>
                <th style={{ padding: '4px 8px' }}>Confidence</th>
                <th style={{ padding: '4px 8px' }}>Reason</th>
                <th style={{ padding: '4px 8px' }}>Bot-approved</th>
              </tr>
            </thead>
            <tbody>
              {linked_prs.map(pr => (
                <tr key={`${pr.org}/${pr.repo}#${pr.pr_number}`} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                  <td style={{ padding: '6px 8px' }}>
                    <Link
                      to={`/repos/${pr.org}/${pr.repo}/pr/${pr.pr_number}`}
                      style={{ color: 'var(--loop-blue-700)' }}
                    >
                      {pr.org}/{pr.repo}#{pr.pr_number}
                    </Link>
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <ConfidenceStars confidence={pr.confidence} />
                  </td>
                  <td style={{ padding: '6px 8px', color: 'var(--loop-neutral-700)' }}>{pr.reason}</td>
                  <td style={{ padding: '6px 8px' }}>
                    {pr.has_bot_approved_artifact ? 'Yes' : 'No'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function Incidents() {
  const [sourceFilter, setSourceFilter] = useState<'' | IncidentSource>('');
  const [archFilter, setArchFilter] = useState<'' | ArchGroup>('');
  const [range, setRange] = useState<'7' | '30' | '90' | 'all'>('30');
  const [withBotApprovedOnly, setWithBotApprovedOnly] = useState(false);
  const [includeLowConfidence, setIncludeLowConfidence] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { idToken, refreshSession } = useAuth();
  const { role, loading: roleLoading } = useRole();
  const qc = useQueryClient();

  const canPoll = !roleLoading && hasRole(role, 'triager');

  // Poll-now button state. The component is the source of truth for the
  // 30s UI window; the API enforces the actual rate limit. We track three
  // things separately because the labels they drive are independent:
  //   - inFlight: showing "Polling…" while waiting for the 202
  //   - lastSuccessfulPollAt: drives "Polled Ns ago" countdown after 202
  //   - rateLimitedUntil: drives "Try again in Ns" after a 429
  const [inFlight, setInFlight] = useState(false);
  const [lastSuccessfulPollAt, setLastSuccessfulPollAt] = useState<number | null>(null);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  // `nowTick` is a re-render trigger only: when a countdown is active we
  // increment it every second so the button label refreshes. Not the actual
  // clock — pollNowButtonState() reads Date.now() at render time.
  const [, setNowTick] = useState(0);
  // setTimeout handle for the auto-refresh that fires once at T+30s after a
  // successful 202. Stored in a ref so a back-to-back click can cancel the
  // prior pending refresh before scheduling a new one.
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tick once per second whenever a countdown is active. The interval auto-
  // expires when there's nothing to count down (lastSuccessfulPollAt has
  // aged past the 30s window AND rateLimitedUntil has passed).
  useEffect(() => {
    const hasActiveCountdown =
      (lastSuccessfulPollAt !== null && Date.now() - lastSuccessfulPollAt < POLL_NOW_WINDOW_MS) ||
      (rateLimitedUntil !== null && Date.now() < rateLimitedUntil);
    if (!hasActiveCountdown) return;
    const id = setInterval(() => setNowTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [lastSuccessfulPollAt, rateLimitedUntil]);

  // Cancel any pending auto-refresh on unmount. The active timer is also
  // cancelled when a new poll is fired (see handlePollNow).
  useEffect(() => {
    return () => {
      if (refetchTimerRef.current !== null) clearTimeout(refetchTimerRef.current);
    };
  }, []);

  async function handlePollNow() {
    if (!idToken) return;
    setInFlight(true);
    setPollError(null);
    try {
      const res = await apiFetch('/api/incidents/poll-now', idToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
      });
      if (res.status === 202) {
        const now = Date.now();
        setLastSuccessfulPollAt(now);
        setRateLimitedUntil(null);
        // Cancel any prior scheduled refresh — a back-to-back click would
        // otherwise pile up timers. Re-schedule for T+30s relative to *this*
        // click.
        if (refetchTimerRef.current !== null) clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = setTimeout(() => {
          void qc.invalidateQueries({ queryKey: ['incidents'] });
          refetchTimerRef.current = null;
        }, POLL_NOW_WINDOW_MS);
      } else if (res.status === 429) {
        const retryAfter = Number(res.headers.get('retry-after') ?? '30');
        const sec = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 30;
        setRateLimitedUntil(Date.now() + sec * 1000);
      } else {
        const text = await res.text().catch(() => '');
        setPollError(`Poll failed (HTTP ${res.status})${text ? `: ${text.slice(0, 200)}` : ''}`);
      }
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        void refreshSession();
        return;
      }
      setPollError(`Poll failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setInFlight(false);
    }
  }

  const buttonState = pollNowButtonState(
    Date.now(),
    lastSuccessfulPollAt,
    rateLimitedUntil,
    inFlight,
  );

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (sourceFilter) params.set('source', sourceFilter);
    if (archFilter) params.set('arch_group', archFilter);
    const since = sinceForRange(range);
    if (since) params.set('since', since);
    if (withBotApprovedOnly) params.set('with_bot_approved_only', 'true');
    if (includeLowConfidence) params.set('include_low_confidence', 'true');
    const s = params.toString();
    return s.length > 0 ? `?${s}` : '';
  }, [sourceFilter, archFilter, range, withBotApprovedOnly, includeLowConfidence]);

  const { data, isLoading, error } = useApiQuery<IncidentListResponse>(
    ['incidents', sourceFilter, archFilter, range, withBotApprovedOnly, includeLowConfidence],
    `/api/incidents${queryString}`
  );

  const rows = data?.incidents ?? [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)', marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 664, margin: 0, color: 'var(--loop-neutral-1100)' }}>Incidents</h2>
        <ConceptHelpIcon slug="incidents" />
      </div>

      <p style={{ color: 'var(--loop-neutral-600)', fontSize: '0.8rem', marginBottom: 'var(--loop-space-200)' }}>
        Every ingested incident (Confluence postmortems + PagerDuty status-page posts), reverse-chronological by occurrence.
        Link counts default to confidence ≥ 50 (high-confidence) — toggle below to include low-confidence heuristic links.
      </p>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={sourceFilter}
          onChange={e => setSourceFilter(e.target.value as '' | IncidentSource)}
          style={{ ...inputStyle, width: 224 }}
          aria-label="Filter by source"
        >
          <option value="">All sources</option>
          <option value="confluence-postmortem">Confluence postmortems</option>
          <option value="pagerduty-statuspage">PagerDuty status-page</option>
        </select>

        <select
          value={archFilter}
          onChange={e => setArchFilter(e.target.value as '' | ArchGroup)}
          style={{ ...inputStyle, width: 160 }}
          aria-label="Filter by arch group"
        >
          <option value="">All arch groups</option>
          <option value="edge">Edge</option>
          <option value="engine">Engine</option>
          <option value="hero">Hero</option>
        </select>

        <select
          value={range}
          onChange={e => setRange(e.target.value as '7' | '30' | '90' | 'all')}
          style={{ ...inputStyle, width: 160 }}
          aria-label="Time range"
        >
          {TIME_RANGES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8125rem', color: 'var(--loop-neutral-800)' }}>
          <input
            type="checkbox"
            checked={withBotApprovedOnly}
            onChange={e => setWithBotApprovedOnly(e.target.checked)}
          />
          Only with bot-approved linked PR
        </label>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8125rem', color: 'var(--loop-neutral-800)' }}>
          <input
            type="checkbox"
            checked={includeLowConfidence}
            onChange={e => setIncludeLowConfidence(e.target.checked)}
          />
          Include low-confidence links
        </label>

        {canPoll && (
          <button
            type="button"
            onClick={handlePollNow}
            disabled={buttonState.disabled}
            title="Force a sync of topology, Confluence postmortems, and PagerDuty status-page posts. Limited to one request per 30s per user."
            style={{
              marginLeft: 'auto',
              padding: '8px 12px',
              border: '1px solid var(--loop-neutral-300)',
              borderRadius: 'var(--loop-radius-sm)',
              background: buttonState.disabled ? 'var(--loop-neutral-100)' : 'var(--loop-blue-700)',
              color: buttonState.disabled ? 'var(--loop-neutral-700)' : 'var(--loop-neutral-0)',
              fontSize: '0.8125rem',
              fontWeight: 528,
              cursor: buttonState.disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {buttonState.label}
          </button>
        )}
      </div>

      {pollError && (
        <p className="error" style={{ fontSize: '0.8125rem', marginBottom: 8 }}>
          {pollError}
        </p>
      )}

      {isLoading && <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading…</p>}
      {error && <p className="error">Error: {String(error)}</p>}

      {data?.truncated && (
        <p style={{ color: 'var(--loop-yellow-800)', fontSize: '0.75rem', marginBottom: 8 }}>
          Showing the first 100 results. Narrow the filters to see more.
        </p>
      )}

      {!isLoading && !error && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
            <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
              <tr>
                {['Title', 'Source', 'Occurred', 'Arch', 'Severity', 'Linked PRs'].map(h => (
                  <th key={h} style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)', fontWeight: 528 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--loop-neutral-500)' }}>
                    No incidents match the current filters.
                  </td>
                </tr>
              )}
              {rows.map(r => {
                const expanded = expandedId === r.incident_id;
                return (
                  <Fragment key={r.incident_id}>
                    <tr
                      style={{ borderTop: '1px solid var(--loop-neutral-200)', cursor: 'pointer' }}
                      onClick={() => setExpandedId(expanded ? null : r.incident_id)}
                      onKeyDown={(e) => {
                        // <tr> isn't natively keyboard-focusable; tabIndex={0} +
                        // Enter/Space handler makes the expand/collapse usable
                        // by keyboard + screen-reader users (aria-expanded
                        // already announces state).
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpandedId(expanded ? null : r.incident_id);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-expanded={expanded}
                      aria-label={`${expanded ? 'Collapse' : 'Expand'} details for ${r.title}`}
                    >
                      <td style={{ padding: '8px 16px' }}>
                        <span style={{ color: 'var(--loop-blue-700)' }}>{truncate(r.title, 50)}</span>
                      </td>
                      <td style={{ padding: '8px 16px' }}>
                        <span title={r.source} style={{ display: 'inline-flex', lineHeight: 1 }}>
                          <SourceIcon source={r.source} />
                        </span>
                      </td>
                      <td style={{ padding: '8px 16px', color: 'var(--loop-neutral-600)' }} title={r.occurred_at}>
                        {relativeTime(r.occurred_at)}
                      </td>
                      <td style={{ padding: '8px 16px' }}><ArchGroupChip arch={r.arch_group} /></td>
                      <td style={{ padding: '8px 16px' }}><SeverityBadge severity={r.severity} /></td>
                      <td style={{ padding: '8px 16px', color: 'var(--loop-neutral-800)' }}>
                        {r.link_count}
                        {r.has_bot_approved_linked_pr && (
                          <span
                            title="At least one linked PR was bot-approved by Diff Vader"
                            style={{
                              marginLeft: 6,
                              padding: '1px 6px',
                              borderRadius: 'var(--loop-radius-xs)',
                              background: 'var(--loop-green-100)',
                              color: 'var(--loop-green-700)',
                              fontSize: '0.625rem',
                              fontWeight: 528,
                            }}
                          >
                            bot-approved
                          </span>
                        )}
                      </td>
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan={6} style={{ padding: 0 }}>
                          <IncidentDetailPanel incidentId={r.incident_id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
